import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getTagInfoforSupplier, getUserProfile, getAllOrders } from "../services/api";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
    Box,
    Button,
    Container,
    Stack,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Chip,
    Grid,
    Divider,
} from "@mui/material";
import { Search } from "lucide-react";
import { useTranslation } from 'react-i18next';


const Check_Tag = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState([]);
    const [filteredTags, setFilteredTags] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingTagId, setDeletingTagId] = useState(null);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [sortMode, setSortMode] = useState('default'); // 'default', 'paid-first', 'pending-first'
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery("(max-width:768px)");

    // Get token from cookies
    const getUserId = () => {
        const id = Cookies.get("id");
        return id;
    };
    const userId = getUserId();

    useEffect(() => {
        if (!userId) {
            navigate(`/login`);
        }
        setLoading(true);
        getUserProfile(userId)
            .then((response) => {
                const userRole = response.data.user.role;
                switch (userRole) {
                    case "user":
                        setLoading(false);
                        navigate("/landing");
                        break;
                    default:
                        setLoading(false);
                        fetchAllTags();
                        return
                }
            })
            .catch((error) => {
                console.error("Error fetching User Role:", error);
                setLoading(false);
            });
    }, [userId]);

    const fetchAllTags = async () => {
        try {
            setLoading(true);
            const response = await getAllOrders(userId);
            
            // Map API response to component state
            // API returns { message, allOrders } where allOrders has _id instead of id
            const ordersData = response.data.allOrders.map(order => ({
                id: order._id,
                tagId: order.tagId,
                orderId: order.orderId,
                petName: order.petName,
                masterEmail: order.masterEmail,
                contact: order.contact,
                location: order.location,
                option: order.option,
                optionSize: order.optionSize,
                optionColor: order.optionColor,
                staffVerification: order.staffVerification,
                pendingStatus: order.pendingStatus,
                cancelled: order.cancelled,
                createdAt: order.createdAt,
            }));
            
            setTags(ordersData);
            setFilteredTags(ordersData);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching tags:", error);
            setError("Failed to load tags");
            setLoading(false);
        }
    };

    // Search and sort handler
    useEffect(() => {
        let filtered = searchText === ""
            ? [...tags]
            : tags.filter(tag => 
                tag.orderId?.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.tagId?.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.petName?.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.contact?.includes(searchText)
            );

        // Apply sorting based on sortMode
        if (sortMode === 'paid-first') {
            filtered = filtered.sort((a, b) => {
                if (a.pendingStatus === b.pendingStatus) return 0;
                return a.pendingStatus ? -1 : 1; // true (paid) comes first
            });
        } else if (sortMode === 'pending-first') {
            filtered = filtered.sort((a, b) => {
                if (a.pendingStatus === b.pendingStatus) return 0;
                return a.pendingStatus ? 1 : -1; // false (pending) comes first
            });
        } else {
            // Default: sort by date (newest first)
            filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        setFilteredTags(filtered);
    }, [searchText, tags, sortMode]);

    const handleView = (tag) => {
        navigate(`/supplier-page?qr=${tag.orderId || tag.tagId}`);
    };

    const handleEdit = (tag) => {
        navigate(`/tag_update_page?qr=${tag.orderId || tag.tagId}`);
    };

    const handleDelete = async (tagId) => {
        setButtonLoading(true);
        try {
            // TODO: Create API endpoint to delete tag
            // await deleteTag(tagId);
            setTags(prev => prev.filter(tag => tag.id !== tagId));
            setFilteredTags(prev => prev.filter(tag => tag.id !== tagId));
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting tag:", error);
            setError("Failed to delete tag");
        }
        setButtonLoading(false);
    };

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const handleStatusSort = () => {
        if (sortMode === 'default') {
            setSortMode('paid-first');
        } else if (sortMode === 'paid-first') {
            setSortMode('pending-first');
        } else {
            setSortMode('default');
        }
    };

    // Mobile card layout
    const renderMobileList = () => (
        <Box>
            {filteredTags.map((tag) => (
                <Box
                    key={tag.id}
                    sx={{
                        background: "#fff",
                        borderRadius: 3,
                        p: 2,
                        mb: 3,
                        boxShadow: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <Chip
                                label={tag.pendingStatus ? t("Paid") : t("Pending")}
                                color={tag.pendingStatus ? "success" : "warning"}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    px: 1.5,
                                    mb: 1,
                                }}
                            />
                            <Box fontWeight={700} fontSize={18}>
                                {tag.orderId}
                            </Box>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                onClick={() => handleView(tag)}
                                title="View"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </IconButton>
                            <IconButton
                                color="primary"
                                onClick={() => handleEdit(tag)}
                                title="Edit"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.5 2.49632C18.8978 2.0985 19.4374 1.875 20 1.875C20.5626 1.875 21.1022 2.0985 21.5 2.49632C21.8978 2.89415 22.1213 3.43371 22.1213 3.99632C22.1213 4.55893 21.8978 5.0985 21.5 5.49632L12 14.9963L8 15.9963L9 11.9963L18.5 2.49632Z" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </IconButton>
                            <IconButton
                                color="error"
                                onClick={() => {
                                    setShowDeleteModal(true);
                                    setDeletingTagId(tag.id);
                                }}
                                title="Delete"
                            >
                                <svg width="22" height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.418 6.4974H23.8346V8.66406H21.668V22.7474C21.668 23.3457 21.183 23.8307 20.5846 23.8307H5.41797C4.81967 23.8307 4.33464 23.3457 4.33464 22.7474V8.66406H2.16797V6.4974H7.58463V3.2474C7.58463 2.64909 8.06966 2.16406 8.66797 2.16406H17.3346C17.933 2.16406 18.418 2.64909 18.418 3.2474V6.4974ZM19.5013 8.66406H6.5013V21.6641H19.5013V8.66406ZM9.7513 11.9141H11.918V18.4141H9.7513V11.9141ZM14.0846 11.9141H16.2513V18.4141H14.0846V11.9141ZM9.7513 4.33073V6.4974H16.2513V4.33073H9.7513Z" fill="#969696" />
                                </svg>
                            </IconButton>
                        </Stack>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography fontWeight={700} fontSize={14}>
                                {t("TagId")}
                            </Typography>
                            <Typography fontSize={14}>
                                {tag.tagId || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontWeight={700} fontSize={14}>
                                {t("petName")}
                            </Typography>
                            <Typography fontSize={14}>
                                {tag.petName || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontWeight={700} fontSize={14}>
                                {t("phoneNumber")}
                            </Typography>
                            <Typography fontSize={14}>
                                {tag.contact}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontWeight={700} fontSize={14}>
                                {t("Item")}
                            </Typography>
                            <Typography fontSize={14}>
                                {tag.option} {tag.optionSize && `(${tag.optionSize}mm)`}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            ))}
            {filteredTags.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                        {searchText ? "No tags found matching your search" : "No tags available"}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ bgcolor: "#fcf8f3", minHeight: "100vh" }}>
            <Navbar />
            {loading && <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    bgcolor: "#fcf8f3",
                }}
            >
                <Typography variant="h6" color="text.secondary">
                    <img
                        src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif"
                        alt="Loading"
                        style={{
                            width: "150px",
                            height: "auto",
                            mixBlendMode: "multiply",
                        }}
                    />
                    <div
                        style={{
                            fontWeight: "500px",
                            fontSize: "15px",
                            textAlign: "center",
                            fontFamily: "Lucida Console",
                        }}
                    >
                        {t('Loading')}
                    </div>
                </Typography>
            </Box>}
            <Box
                sx={{
                    maxWidth: isMobile ? 1200 : 1440,
                    mx: "auto",
                    pt: 12,
                    px: isMobile ? 2 : 4,
                    pb: 4,
                }}
            >
                <Box sx={{ mt: isMobile ? 2 : 4, mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            mb: 3,
                            fontWeight: "bold",
                            fontSize: { xs: "24px", sm: "28px", md: "32px" },
                        }}
                    >
                        {t("SearchPTags")}
                    </Typography>

                    {/* Search Bar */}
                    <TextField
                        fullWidth
                        placeholder={t('EnterID')}
                        value={searchText}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={20} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: "white",
                                borderRadius: "8px",
                                height: "48px",
                            },
                        }}
                        sx={{ mb: 3, maxWidth: 600 }}
                    />

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}
                </Box>

                {/* Conditional Rendering: Mobile vs Desktop */}
                {isMobile ? (
                    renderMobileList()
                ) : (
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            borderRadius: 2, 
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            mb: 4,
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ background: "#F8F8F8" }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("orderId")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("TagId")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("petName")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("phoneNumber")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("Item")}</TableCell>
                                    <TableCell 
                                        onClick={handleStatusSort}
                                        sx={{ 
                                            fontWeight: 700, 
                                            fontSize: 14,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': {
                                                backgroundColor: '#E8E8E8'
                                            }
                                        }}
                                    >
                                        {t("PaymentStatus")}
                                        {sortMode === 'paid-first' && ' ↓'}
                                        {sortMode === 'pending-first' && ' ↑'}
                                    </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("Actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTags.map((tag) => (
                                <TableRow key={tag.id} hover>
                                    <TableCell>
                                        <Typography fontWeight={500}>{tag.orderId}</Typography>
                                    </TableCell>
                                    <TableCell>{tag.tagId}</TableCell>
                                    <TableCell>{tag.petName || '-'}</TableCell>
                                    <TableCell>{tag.contact}</TableCell>
                                    <TableCell>
                                        {tag.option} {tag.optionSize && `(${tag.optionSize}mm)`}
                                        {tag.optionColor && ` - ${tag.optionColor}`}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tag.pendingStatus ? t("Paid") : t("Pending")}
                                            color={tag.pendingStatus ? "success" : "warning"}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                color="default"
                                                onClick={() => handleView(tag)}
                                                title="View"
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </IconButton>
                                            <IconButton 
                                                color="primary" 
                                                onClick={() => handleEdit(tag)}
                                                title="Edit"
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M18.5 2.49632C18.8978 2.0985 19.4374 1.875 20 1.875C20.5626 1.875 21.1022 2.0985 21.5 2.49632C21.8978 2.89415 22.1213 3.43371 22.1213 3.99632C22.1213 4.55893 21.8978 5.0985 21.5 5.49632L12 14.9963L8 15.9963L9 11.9963L18.5 2.49632Z" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() => {
                                                    setShowDeleteModal(true);
                                                    setDeletingTagId(tag.id);
                                                }}
                                                title="Delete"
                                            >
                                                <svg width="22" height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M18.418 6.4974H23.8346V8.66406H21.668V22.7474C21.668 23.3457 21.183 23.8307 20.5846 23.8307H5.41797C4.81967 23.8307 4.33464 23.3457 4.33464 22.7474V8.66406H2.16797V6.4974H7.58463V3.2474C7.58463 2.64909 8.06966 2.16406 8.66797 2.16406H17.3346C17.933 2.16406 18.418 2.64909 18.418 3.2474V6.4974ZM19.5013 8.66406H6.5013V21.6641H19.5013V8.66406ZM9.7513 11.9141H11.918V18.4141H9.7513V11.9141ZM14.0846 11.9141H16.2513V18.4141H14.0846V11.9141ZM9.7513 4.33073V6.4974H16.2513V4.33073H9.7513Z" fill="#969696" />
                                                </svg>
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredTags.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {searchText ? "No tags found matching your search" : "No tags available"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                )}
            </Box>

            <Footer />

            {/* Delete Confirmation Modal */}
            <Dialog
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        maxWidth: 400,
                        width: "100%",
                        mx: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                        pt: 4,
                        pb: 2,
                        fontWeight: 700,
                        fontSize: 20,
                    }}
                >
                    {t('ConfirmCancel')}
                </DialogTitle>
                <DialogContent sx={{ textAlign: "center", pb: 2 }}>
                    <Typography sx={{ color: "#666", fontSize: 16 }}>
                        {t('CancelOrderTag')}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        flexDirection: "column",
                        px: 3,
                        pb: 3,
                        "& > :not(style) ~ :not(style)": {
                            marginLeft: 0,
                        },
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{
                            backgroundColor: "#FF1A1A",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 16,
                            borderRadius: 2,
                            py: 1.5,
                            mb: 1,
                            textTransform: 'none',
                            "&:hover": { backgroundColor: "#d63e3e" },
                            cursor: buttonLoading ? "not-allowed" : "pointer",
                        }}
                        onClick={() => handleDelete(deletingTagId)}
                        disabled={buttonLoading}
                    >
                        {t('Delete')}
                        {buttonLoading && (
                            <CircularProgress 
                                size={20} 
                                sx={{ ml: 1, color: "inherit" }} 
                            />
                        )}
                    </Button>
                    <Button
                        fullWidth
                        variant="text"
                        sx={{
                            color: "#B0B0B0",
                            fontWeight: 700,
                            fontSize: 16,
                            borderRadius: 2,
                            py: 1.5,
                            textTransform: 'none',
                        }}
                        onClick={() => setShowDeleteModal(false)}
                    >
                        {t('cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Check_Tag;
